import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    getCollection,
    updateCollection,
    getCollections,
    type CollectionDTO,
    type FAQItem,
} from "../helpers/Collections";
import {
    getAttributes,
    getAttributeValues,
    // type AttributeListItem,
    // type AttributeValueItem,
} from "../helpers/Attributes";

type SelectedValue = {
    id: number;
    value: string;
    attribute_id?: number;
    attribute_name?: string;
};

export default function CollectionEdit() {
    const { id } = useParams<{ id: string }>();
    const collectionId = Number(id);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // --- base fields
    const [name, setName] = useState("");
    const [shortDescription, setShortDescription] = useState("");

    // --- attributes picking
    const [attributes, setAttributes] = useState<any[]>([]);
    const [activeAttributeId, setActiveAttributeId] = useState<number | "">("");
    const [attrValues, setAttrValues] = useState<any[]>([]);
    const [attrSearch, setAttrSearch] = useState("");
    const [selectedValues, setSelectedValues] = useState<Record<number, SelectedValue>>(
        {}
    ); // attribute_value_id -> info

    // --- similar collections
    const [similar, setSimilar] = useState<number[]>([]);
    const [similarSearch, setSimilarSearch] = useState("");
    const [similarResults, setSimilarResults] = useState<CollectionDTO[]>([]);

    // --- FAQ
    const [faq, setFaq] = useState<FAQItem[]>([]);

    const abortRef = useRef<AbortController | null>(null);

    // ============ Load initial ============

    const loadCollection = useCallback(async () => {
        setLoading(true);
        try {
            const col = await getCollection(collectionId);
            setName(col.name || "");
            setShortDescription(col.short_description || "");
            setFaq(col.settings?.faq ?? []);
            const vals = col.settings?.attributes ?? [];
            // сохраним как пустые описатели — пополняем их по мере загрузки
            const initialMap: Record<number, SelectedValue> = {};
            vals.forEach((vid: number) => {
                initialMap[vid] = { id: vid, value: String(vid) };
            });
            setSelectedValues(initialMap);
            setSimilar(col.settings?.similar_collections ?? []);
        } catch (e) {
            console.error(e);
            alert("Failed to load collection");
        } finally {
            setLoading(false);
        }
    }, [collectionId]);

    const loadAttributes = useCallback(async () => {
        try {
            // возьмём побольше, чтобы не пагинировать в UI
            const list = await getAttributes(500, 1, null, {});
            // поддержим оба возможных формата ответа
            const items: AttributeListItem[] = Array.isArray(list?.data) ? list.data : list;
            setAttributes(items || []);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        if (!Number.isFinite(collectionId)) return;
        loadCollection();
        loadAttributes();
    }, [collectionId, loadCollection, loadAttributes]);

    // ============ Load attribute values (with search & abort) ============

    const fetchAttrValues = useCallback(
        async (attributeId: number, search: string) => {
            try {
                if (abortRef.current) abortRef.current.abort();
                abortRef.current = new AbortController();
                const res = await getAttributeValues(attributeId, search, {
                    signal: abortRef.current.signal,
                });
                const arr: AttributeValueItem[] = Array.isArray(res?.data) ? res.data : res;
                setAttrValues(arr || []);
            } catch (e: any) {
                if (e?.name !== "AbortError") {
                    console.error(e);
                }
            }
        },
        []
    );

    // дебаунс поиска по значениям
    const attrSearchTimer = useRef<number>();
    const onAttrSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setAttrSearch(v);
        if (attrSearchTimer.current) window.clearTimeout(attrSearchTimer.current);
        attrSearchTimer.current = window.setTimeout(() => {
            if (activeAttributeId && typeof activeAttributeId === "number") {
                fetchAttrValues(activeAttributeId, v);
            }
        }, 300) as unknown as number;
    };

    // при смене активного атрибута — подгружать значения
    useEffect(() => {
        if (activeAttributeId && typeof activeAttributeId === "number") {
            fetchAttrValues(activeAttributeId, attrSearch);
        } else {
            setAttrValues([]);
        }
    }, [activeAttributeId, fetchAttrValues]); // attrSearch дебаунсится отдельно

    // ============ Attribute value selection ============

    const activeAttributeName = useMemo(() => {
        if (!activeAttributeId) return undefined;
        return attributes.find((a) => a.id === activeAttributeId)?.name;
    }, [activeAttributeId, attributes]);

    const toggleValue = (v: AttributeValueItem, checked: boolean) => {
        setSelectedValues((map) => {
            const next = { ...map };
            if (checked) {
                next[v.id] = {
                    id: v.id,
                    value: v.value,
                    attribute_id: v.attribute_id,
                    attribute_name: activeAttributeName,
                };
            } else {
                delete next[v.id];
            }
            return next;
        });
    };

    const removeSelectedValue = (id: number) => {
        setSelectedValues((m) => {
            const next = { ...m };
            delete next[id];
            return next;
        });
    };

    // ============ Similar collections search ============

    const loadSimilarResults = useCallback(
        async (q: string) => {
            try {
                if (!q) {
                    setSimilarResults([]);
                    return;
                }
                const res = await getCollections(10, 1, q, {});
                setSimilarResults(res?.data ?? []);
            } catch (e) {
                console.error(e);
            }
        },
        []
    );

    const similarTimer = useRef<number>();
    const onSimilarSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setSimilarSearch(v);
        if (similarTimer.current) window.clearTimeout(similarTimer.current);
        similarTimer.current = window.setTimeout(() => loadSimilarResults(v), 300) as unknown as number;
    };

    const addSimilar = (cid: number) => {
        setSimilar((arr) => {
            if (arr.includes(cid) || cid === collectionId) return arr;
            return [...arr, cid];
        });
    };
    const removeSimilar = (cid: number) => {
        setSimilar((arr) => arr.filter((x) => x !== cid));
    };

    // ============ FAQ ============

    const addFaqItem = () => setFaq((f) => [...f, { question: "", answer: "" }]);
    const updateFaqItem = (idx: number, patch: Partial<FAQItem>) =>
        setFaq((f) => f.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    const removeFaqItem = (idx: number) =>
        setFaq((f) => f.filter((_, i) => i !== idx));

    // ============ Save ============

    const onSave = async () => {
        try {
            setSaving(true);
            const attrIds = Object.keys(selectedValues).map((k) => Number(k));
            await updateCollection(collectionId, {
                name,
                shortDescription,
                attributes: attrIds,
                similarCollections: similar,
                faq,
            });
            alert("Collection saved");
        } catch (e) {
            console.error(e);
            alert("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const selectedAttrArray = useMemo(
        () =>
            Object.values(selectedValues).sort((a, b) =>
                (a.attribute_name || "").localeCompare(b.attribute_name || "") ||
                a.value.localeCompare(b.value)
            ),
        [selectedValues]
    );

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">Edit collection</h1>
                    <div className="text-sm text-gray-600">
                        ID: {collectionId} •{" "}
                        <Link className="text-blue-600 underline" to="/collections">
                            back to list
                        </Link>
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        className="px-3 py-1 rounded border"
                        onClick={() => loadCollection()}
                        disabled={loading || saving}
                    >
                        Reload
                    </button>
                    <button
                        className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
                        onClick={onSave}
                        disabled={loading || saving}
                    >
                        Save
                    </button>
                </div>
            </div>

            <div className="rounded bg-white p-3">
                {loading ? (
                    <div className="p-6 text-gray-600">Loading…</div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left: base fields */}
                        <div className="lg:col-span-1 space-y-3">
                            <div>
                                <label className="block text-sm mb-1">Name</label>
                                <input
                                    className="border px-2 py-1 rounded w-full"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    maxLength={120}
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-1">
                                    Short description <span className="text-gray-400">(≤255)</span>
                                </label>
                                <textarea
                                    className="border px-2 py-1 rounded w-full"
                                    rows={4}
                                    value={shortDescription}
                                    onChange={(e) => setShortDescription(e.target.value)}
                                    maxLength={255}
                                />
                            </div>

                            {/* Similar collections */}
                            <div className="pt-2">
                                <div className="font-medium mb-2">Similar collections</div>
                                <input
                                    className="border px-2 py-1 rounded w-full mb-2"
                                    placeholder="Search collections…"
                                    value={similarSearch}
                                    onChange={onSimilarSearchChange}
                                />
                                {similarResults.length > 0 ? (
                                    <div className="border rounded max-h-48 overflow-auto">
                                        {similarResults.map((c) => {
                                            const disabled = similar.includes(c.id) || c.id === collectionId;
                                            return (
                                                <div
                                                    key={c.id}
                                                    className="flex items-center justify-between px-2 py-1 border-b last:border-0"
                                                >
                                                    <div className="text-sm">
                                                        {c.name} <span className="text-gray-500">#{c.id}</span>
                                                    </div>
                                                    <button
                                                        className="text-blue-600 disabled:text-gray-400"
                                                        onClick={() => addSimilar(c.id)}
                                                        disabled={disabled}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                                {similar.length > 0 ? (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {similar.map((cid) => (
                                            <span
                                                key={cid}
                                                className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-xs border"
                                            >
                                                #{cid}
                                                <button
                                                    className="text-red-600"
                                                    onClick={() => removeSimilar(cid)}
                                                    title="Remove"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {/* Middle: attribute value browser */}
                        <div className="lg:col-span-1 space-y-3">
                            <div className="font-medium">Filter values (attribute → values)</div>

                            <div className="flex gap-2">
                                <select
                                    className="border px-2 py-1 rounded min-w-[240px]"
                                    value={activeAttributeId}
                                    onChange={(e) =>
                                        setActiveAttributeId(e.target.value ? Number(e.target.value) : "")
                                    }
                                >
                                    <option value="">— choose attribute —</option>
                                    {attributes.map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {a.name} (#{a.id})
                                        </option>
                                    ))}
                                </select>

                                <input
                                    className="border px-2 py-1 rounded flex-1"
                                    placeholder="Search values…"
                                    value={attrSearch}
                                    onChange={onAttrSearchChange}
                                    disabled={activeAttributeId === ""}
                                />
                            </div>

                            {activeAttributeId === "" ? (
                                <div className="text-gray-600">Select an attribute to see values.</div>
                            ) : attrValues.length === 0 ? (
                                <div className="text-gray-600">No values.</div>
                            ) : (
                                <div className="border rounded max-h-[420px] overflow-auto">
                                    {attrValues.map((v) => {
                                        const isChecked = !!selectedValues[v.id];
                                        return (
                                            <div
                                                key={v.id}
                                                className="flex items-center justify-between px-2 py-1 border-b last:border-0"
                                            >
                                                <label className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked ? true : false}
                                                        onChange={(e) => toggleValue(v, e.target.checked)}
                                                    />
                                                    <span className="font-medium">{v.value}</span>
                                                    <span className="text-gray-500">#{v.id}</span>
                                                </label>
                                                <span className="text-xs text-gray-500">
                                                    {typeof v.product_count === "number"
                                                        ? `${v.product_count} products`
                                                        : "—"}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Right: selected values + FAQ */}
                        <div className="lg:col-span-1 space-y-4">
                            <div>
                                <div className="font-medium mb-2">Selected values ({Object.keys(selectedValues).length})</div>
                                {selectedAttrArray.length === 0 ? (
                                    <div className="text-gray-600">No values selected yet.</div>
                                ) : (
                                    <div className="border rounded max-h-[220px] overflow-auto">
                                        {selectedAttrArray.map((sv) => (
                                            <div
                                                key={sv.id}
                                                className="flex items-center justify-between px-2 py-1 border-b last:border-0"
                                            >
                                                <div className="text-sm">
                                                    <div className="font-medium">{sv.value}</div>
                                                    <div className="text-xs text-gray-500">
                                                        #{sv.id} {sv.attribute_name ? `• ${sv.attribute_name}` : ""}
                                                    </div>
                                                </div>
                                                <button
                                                    className="text-red-600 text-sm"
                                                    onClick={() => removeSelectedValue(sv.id)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="font-medium mb-2">FAQ</div>
                                <div className="space-y-2">
                                    {faq.length === 0 ? (
                                        <div className="text-gray-600">No FAQ items yet.</div>
                                    ) : null}
                                    {faq.map((item, idx) => (
                                        <div key={idx} className="border rounded p-2 space-y-2">
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-1">
                                                    Question
                                                </label>
                                                <input
                                                    className="border px-2 py-1 rounded w-full"
                                                    value={item.question}
                                                    onChange={(e) =>
                                                        updateFaqItem(idx, { question: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-1">
                                                    Answer
                                                </label>
                                                <textarea
                                                    className="border px-2 py-1 rounded w-full"
                                                    rows={3}
                                                    value={item.answer}
                                                    onChange={(e) =>
                                                        updateFaqItem(idx, { answer: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <div className="flex justify-end">
                                                <button
                                                    className="text-red-600 text-sm"
                                                    onClick={() => removeFaqItem(idx)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button className="px-3 py-1 rounded border" onClick={addFaqItem}>
                                        + Add FAQ item
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer actions duplicate */}
            <div className="flex justify-end gap-2">
                <Link to="/collections" className="px-3 py-1 rounded border">
                    Cancel
                </Link>
                <button
                    className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
                    onClick={onSave}
                    disabled={loading || saving}
                >
                    Save
                </button>
            </div>
        </div>
    );
}